import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { Download, UploadFile } from "@mui/icons-material";
import { Dialog } from "@/components/Dialog/Dialog";
import { useSnackbar } from "@/components/Snackbar";
import { parseSpendSheetConfig, SpendSheetConfig } from "./spendSheetConfig";

interface Props {
  open: boolean;
  onClose: () => void;
  currentConfigJson: string;
  onLoad: (config: SpendSheetConfig) => void;
}

const CONFIG_FILE_NAME = "marketanalyzer-export-config.json";

export const SpendSheetConfigDialog: React.FC<Props> = ({
  open,
  onClose,
  currentConfigJson,
  onLoad,
}) => {
  const { showSuccess } = useSnackbar();
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear any stale error each time the dialog opens.
  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleDownload = () => {
    const blob = new Blob([currentConfigJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = CONFIG_FILE_NAME;
    anchor.click();
    URL.revokeObjectURL(url);
    showSuccess("Configuration downloaded.");
  };

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith(".json")) {
      setError("Please select a .json configuration file.");
      return;
    }

    let text: string;
    try {
      text = await file.text();
    } catch {
      setError("Could not read the selected file.");
      return;
    }

    const result = parseSpendSheetConfig(text);
    if (!result.ok) {
      // Surface the error inline (persistent) so the HGer can see what's wrong.
      setError(result.error);
      return;
    }

    setError(null);
    onLoad(result.config);
    showSuccess("Spend Sheet configuration loaded.");
    onClose();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFile(file);
    // Reset so selecting the same file again still fires onChange.
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  return (
    <Dialog
      open={open}
      title="Spend Sheet Configuration"
      onClose={onClose}
      actions={
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          width="100%"
          padding={1}
        >
          <Button
            data-testid="spend-sheet-config-close-btn"
            onClick={onClose}
            color="secondary"
            variant="outlined"
          >
            Close
          </Button>
        </Stack>
      }
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Download your current Spend Sheet configuration to reuse or share
            it, or load a saved configuration file. Loading replaces the current
            IT Spend, custom columns, and Cloud Maturity settings.
          </Typography>

          <Button
            data-testid="spend-sheet-config-download-btn"
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{ alignSelf: "flex-start" }}
          >
            Download configuration
          </Button>

          {error && (
            <Alert severity="error" data-testid="spend-sheet-config-error">
              {error}
            </Alert>
          )}

          <Box
            data-testid="spend-sheet-config-dropzone"
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              minHeight: 180,
              p: 3,
              cursor: "pointer",
              textAlign: "center",
              color: "text.secondary",
              border: 2,
              borderStyle: "dashed",
              borderColor: isDragging ? "primary.main" : "divider",
              borderRadius: 1,
              bgcolor: isDragging ? "action.hover" : "transparent",
              transition:
                "border-color 150ms ease, background-color 150ms ease",
            }}
          >
            <UploadFile
              fontSize="large"
              color={isDragging ? "primary" : "inherit"}
            />
            <Typography variant="body2">
              Drag &amp; drop a configuration file here, or click to browse
            </Typography>
            <Typography variant="caption">Accepts a .json file</Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              data-testid="spend-sheet-config-file-input"
              onChange={handleInputChange}
            />
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
};
